import * as React from 'react';
import { useHubConnectionState } from '@tyndo/hooks';
import { cn } from '../../lib/utils';

export type FreshnessConnectionState =
  | 'live'
  | 'reconnecting'
  | 'offline'
  | 'replay';

export interface LiveFreshnessIndicatorProps {
  /**
   * When the underlying data was last updated. Either pass `lastUpdate`
   * (Date / string / number) for the canonical API, or `timestamp` as a
   * shorthand alias accepted by the bulk-adoption pattern. Both are
   * equivalent; if both are passed, `lastUpdate` wins.
   */
  lastUpdate?: Date | string | number | null;
  /**
   * Shorthand alias for `lastUpdate` — accepts ISO strings (such as the
   * `__freshAt` field in Tyndo response envelopes), Date objects, or
   * numeric epochs.
   */
  timestamp?: Date | string | number | null;
  /**
   * Optional explicit hub / channel state. When omitted, the indicator
   * auto-detects state from the SignalR hub named in `hub` (if any).
   * If neither `connectionState` nor `hub` is supplied the indicator
   * defaults to `'live'` and relies solely on `staleAfterSeconds` to
   * downgrade the label.
   */
  connectionState?: FreshnessConnectionState;
  /**
 * Name of the SignalR hub the indicator should subscribe
   * to for auto-detected disconnect downgrades. When the hub reports
   * `'reconnecting'` or `'offline'`, the indicator renders a stale
   * label automatically. Common values: `'alarms'`, `'equipment'`,
   * `'production'`, `'notifications'`.
   */
  hub?: string;
  /** Threshold (seconds) past which a `live` state is rendered as stale. */
  staleAfterSeconds?: number;
  /** Compact mode hides the relative-time label. */
  compact?: boolean;
  className?: string;
}

const STATE_COLOR: Record<FreshnessConnectionState, string> = {
  live: 'hsl(var(--state-running))',
  reconnecting: 'hsl(var(--alarm-minor))',
  offline: 'hsl(var(--alarm-critical))',
  replay: 'hsl(var(--alarm-advisory))',
};

const STATE_LABEL: Record<FreshnessConnectionState, string> = {
  live: 'Live',
  reconnecting: 'Reconnecting',
  offline: 'Offline',
  replay: 'Replay',
};

function toDate(input: Date | string | number | null): Date | null {
  if (input === null || input === undefined) return null;
  if (input instanceof Date) return Number.isFinite(input.getTime()) ? input : null;
  const d = new Date(input);
  return Number.isFinite(d.getTime()) ? d : null;
}

function formatRelative(deltaMs: number): string {
  const s = Math.round(deltaMs / 1000);
  if (s < 5) return 'just now';
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  return `${d}d ago`;
}

/**
 * Tyndo LiveFreshnessIndicator — pulse dot + relative time + state label.
 *
 * Drop into a PageHeader meta slot or next to a header counter so operators
 * can see at a glance whether the data they're acting on is fresh, stale,
 * or coming from a replay buffer.
 */
export function LiveFreshnessIndicator({
  lastUpdate,
  timestamp,
  connectionState,
  hub,
  staleAfterSeconds = 60,
  compact,
  className,
}: LiveFreshnessIndicatorProps) {
  const [now, setNow] = React.useState<number>(() => Date.now());

  React.useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  // `lastUpdate` and `timestamp` are aliases — `lastUpdate` wins if
  // both are passed.
  const rawTime = lastUpdate !== undefined ? lastUpdate : timestamp ?? null;
  const updated = toDate(rawTime);
  const deltaMs = updated ? now - updated.getTime() : null;

 // Closes from docs/latest-analysis.md.
  // When the caller supplies `hub`, the inner <HubAwareLabel> subscribes
  // to that SignalR hub via `useHubConnectionState` and downgrades the
  // indicator to `'reconnecting'` / `'offline'` whenever the hub state
  // reports a disconnect. An explicit `connectionState` prop always wins
  // so legacy callers keep working unchanged.
  if (hub && !connectionState) {
    return (
      <HubAwareIndicator
        hub={hub}
        deltaMs={deltaMs}
        staleAfterSeconds={staleAfterSeconds}
        compact={compact}
        className={className}
      />
    );
  }

  return (
    <FreshnessChrome
      baseState={connectionState ?? 'live'}
      deltaMs={deltaMs}
      staleAfterSeconds={staleAfterSeconds}
      compact={compact}
      className={className}
    />
  );
}

/**
 * Sub-component used when the caller supplies `hub`. Calls
 * `useHubConnectionState` so the indicator auto-detects SignalR
 * disconnects without forcing every parent to wire up the hook.
 *
 * Pulled out so the parent only calls the hook when it actually has a
 * hub name to subscribe to (rules of hooks: don't subscribe to an
 * empty hub name and spawn unwanted connections).
 */
interface HubAwareIndicatorProps {
  hub: string;
  deltaMs: number | null;
  staleAfterSeconds: number;
  compact?: boolean;
  className?: string;
}

function HubAwareIndicator({
  hub,
  deltaMs,
  staleAfterSeconds,
  compact,
  className,
}: HubAwareIndicatorProps) {
  const hubState = useHubConnectionState(hub);
  return (
    <FreshnessChrome
      baseState={hubState}
      deltaMs={deltaMs}
      staleAfterSeconds={staleAfterSeconds}
      compact={compact}
      className={className}
    />
  );
}

/**
 * Shared chrome — given a resolved `baseState` and `deltaMs`, render the
 * pulse dot + label + relative-time. Both the public component and the
 * hub-aware sub-component flow through here so they look identical.
 */
interface FreshnessChromeProps {
  baseState: FreshnessConnectionState;
  deltaMs: number | null;
  staleAfterSeconds: number;
  compact?: boolean;
  className?: string;
}

function FreshnessChrome({
  baseState,
  deltaMs,
  staleAfterSeconds,
  compact,
  className,
}: FreshnessChromeProps) {
  const stale =
    baseState === 'live' &&
    deltaMs !== null &&
    deltaMs > staleAfterSeconds * 1000;

  const effectiveState: FreshnessConnectionState = stale
    ? 'reconnecting'
    : baseState;
  const color = STATE_COLOR[effectiveState];
  const pulsing = effectiveState === 'live';
  const label = STATE_LABEL[effectiveState];

  let timeLabel: string;
  if (effectiveState === 'offline') {
    timeLabel = 'Offline';
  } else if (deltaMs === null) {
    timeLabel = 'No data';
  } else if (stale) {
    timeLabel = `Stale ${formatRelative(deltaMs)}`;
  } else {
    timeLabel = formatRelative(deltaMs);
  }

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-md border border-border bg-surface-raised px-2 py-1 text-[11px] text-muted-foreground',
        compact && 'gap-1 px-1.5 py-0.5',
        className
      )}
      aria-live="polite"
      title={`${label} · ${timeLabel}`}
    >
      <span
        aria-hidden
        className="relative flex h-2 w-2 items-center justify-center"
      >
        {pulsing && (
          <span
            className="absolute inline-flex h-full w-full rounded-full opacity-50 tyndo-pulse"
            style={{ backgroundColor: color }}
          />
        )}
        <span
          className="relative inline-flex h-2 w-2 rounded-full"
          style={{ backgroundColor: color }}
        />
      </span>
      <span
        className="t-mono uppercase tracking-wider"
        style={{ color }}
      >
        {label}
      </span>
      {!compact && (
        <span className="t-tabular tabular-nums">{timeLabel}</span>
      )}
    </span>
  );
}
